import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { AuthProvider } from './context/AuthContext';
import MainLayout from './components/MainLayout';
import { SHOW_MIDWIVES_REVIEWER } from './config/featureFlags';
import { useAuth } from './hooks/useAuth';
import SubscriptionGate from './components/SubscriptionGate';

const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Handbook = React.lazy(() => import('./pages/Handbook'));
const PracticeTests = React.lazy(() => import('./pages/PracticeTests'));
const MockExam = React.lazy(() => import('./pages/MockExam'));
const TestStrategies = React.lazy(() => import('./pages/TestStrategies'));
const Progress = React.lazy(() => import('./pages/Progress'));
const ExamSimulation = React.lazy(() => import('./pages/ExamSimulation'));
const Subscribe = React.lazy(() => import('./pages/Subscribe'));
const PaymentSuccess = React.lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancel = React.lazy(() => import('./pages/PaymentCancel'));
const FAQ = React.lazy(() => import('./pages/FAQ'));
const ContactUs = React.lazy(() => import('./pages/ContactUs'));
const FreeSampleTest = React.lazy(() => import('./pages/FreeSampleTest'));
const FreeGulfRNReviewer = React.lazy(() => import('./pages/FreeGulfRNReviewer'));
const FreeReviewerDownload = React.lazy(() => import('./pages/FreeReviewerDownload'));
const Admin = React.lazy(() => import('./pages/Admin'));
const LegalPolicy = React.lazy(() => import('./pages/LegalPolicy'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const MidwivesReviewer = React.lazy(() => import('./pages/MidwivesReviewer'));
const MidwiferyDashboard = React.lazy(() => import('./pages/midwifery/MidwiferyDashboard'));
const MidwiferyFAQ = React.lazy(() => import('./pages/midwifery/MidwiferyFAQ'));
const MidwiferyContactUs = React.lazy(() => import('./pages/midwifery/MidwiferyContactUs'));
const MidwiferyLegalPolicy = React.lazy(() => import('./pages/midwifery/MidwiferyLegalPolicy'));
const MidwiferySubscribe = React.lazy(() => import('./pages/midwifery/MidwiferySubscribe'));
const MidwiferyProgress = React.lazy(() => import('./pages/midwifery/MidwiferyProgress'));
const MidwifeFreePractice = React.lazy(() => import('./pages/midwifery/MidwifeFreePractice'));
const MidwifeSampleModule = React.lazy(() => import('./pages/midwifery/MidwifeSampleModule'));
const MidwifeTrialMock = React.lazy(() => import('./pages/midwifery/MidwifeTrialMock'));
const MidwifeFreeTrial = React.lazy(() => import('./pages/midwifery/MidwifeFreeTrial'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

function PageLoader() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );
}

function TrackSwitch({ nursing, midwifery }: { nursing: React.ReactNode; midwifery: React.ReactNode }) {
  const { track } = useAuth();
  return <>{track === 'midwifery' ? midwifery : nursing}</>;
}

function MidwivesGate({ children }: { children: React.ReactNode }) {
  const { isLoading, track } = useAuth();
  if (isLoading) return null;
  if (track !== 'midwifery') return <Navigate to="/" replace />;
  return <SubscriptionGate featureName="the Midwife Reviewer">{children}</SubscriptionGate>;
}

console.log('🗓 App is unlocked and accessible');

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
});

function App() {
  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/free-gulf-rn-reviewer" element={<FreeGulfRNReviewer />} />
              <Route path="/free-reviewer/download" element={<FreeReviewerDownload />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/*" element={
                <MainLayout>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<TrackSwitch nursing={<Dashboard />} midwifery={<MidwiferyDashboard />} />} />
                      <Route path="/handbook" element={<TrackSwitch nursing={<Handbook />} midwifery={<Navigate to="/" replace />} />} />
                      <Route path="/practice-tests" element={<TrackSwitch nursing={<PracticeTests />} midwifery={<Navigate to="/" replace />} />} />
                      <Route path="/mock-exam" element={<TrackSwitch nursing={<MockExam />} midwifery={<Navigate to="/" replace />} />} />
                      <Route path="/strategies" element={<TrackSwitch nursing={<TestStrategies />} midwifery={<Navigate to="/" replace />} />} />
                      <Route path="/progress" element={<TrackSwitch nursing={<Progress />} midwifery={<MidwivesGate><MidwiferyProgress /></MidwivesGate>} />} />
                      <Route path="/subscribe" element={<TrackSwitch nursing={<Subscribe />} midwifery={<MidwiferySubscribe />} />} />
                      <Route path="/midwifery/subscribe" element={<MidwiferySubscribe />} />
                      <Route path="/midwives/trial" element={<MidwifeFreeTrial />} />
                      <Route path="/midwives/free" element={<MidwifeFreePractice />} />
                      <Route path="/midwives/trial-mock" element={<MidwifeTrialMock />} />
                      <Route path="/midwives/sample-module" element={<MidwifeSampleModule />} />
                      <Route path="/payment/success" element={<PaymentSuccess />} />
                      <Route path="/rn/payment-success" element={<PaymentSuccess />} />
                      <Route path="/payment/cancel" element={<PaymentCancel />} />
                      <Route path="/exam/:examType" element={<ExamSimulation />} />
                      <Route path="/free-sample" element={<FreeSampleTest />} />
                      <Route path="/admin" element={<Admin />} />
                      <Route path="/faq" element={<TrackSwitch nursing={<FAQ />} midwifery={<MidwiferyFAQ />} />} />
                      <Route path="/contact" element={<TrackSwitch nursing={<ContactUs />} midwifery={<MidwiferyContactUs />} />} />
                      <Route path="/legal" element={<TrackSwitch nursing={<LegalPolicy />} midwifery={<MidwiferyLegalPolicy />} />} />
                      {SHOW_MIDWIVES_REVIEWER && <Route path="/midwives" element={<MidwivesGate><MidwivesReviewer key="midwives-home" /></MidwivesGate>} />}
                      {SHOW_MIDWIVES_REVIEWER && <Route path="/midwives/study" element={<MidwivesGate><MidwivesReviewer key="midwives-study" initialView="study" /></MidwivesGate>} />}
                      {SHOW_MIDWIVES_REVIEWER && <Route path="/midwives/mock" element={<MidwivesGate><MidwivesReviewer key="midwives-mock" initialView="mock-setup" /></MidwivesGate>} />}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </MainLayout>
              } />
            </Routes>
          </Suspense>
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
